import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/root';
import { setSelectedInvoicesDataToPay, setTotalStripeAmount } from '../../../../../redux/slices/feesSlice';
import { currency } from '../../../utils';
import BigScreenTable from './InvoicesTable/BigScreenTable';
import SmallScreenTable from './InvoicesTable/SmallScreenTable';
import { getInvoiceBalance } from '../../../../../Components/InvoiceTool/InvoiceUtil';

export const PHONE = '480px';
export const TABLET = '850px';
export const CCFee = 0.0299;

const StripeInvoicesTable = () => {
  const { selectedProjectInvoice, selectedInvoicesToPay, selectedInvoicesDataToPay, totalStripeAmount } = useSelector(
    (s: RootState) => s.fees,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const clearInvoiceNumber = invoiceNumber => {
      if (invoiceNumber.indexOf('----') > -1) {
        return invoiceNumber.split('----')[0];
      }
      return invoiceNumber;
    };
    if (selectedProjectInvoice.permit) {
      const { invoices: _invoices } = selectedProjectInvoice.permit;
      let _total = 0;
      const tableData = _invoices.reduce((invoiceArr, invoice) => {
        if (selectedInvoicesToPay[invoice.id]) {
          _total += Number(getInvoiceBalance(invoice));
          invoiceArr.push({ ...invoice, invoiceNumber: clearInvoiceNumber(invoice.invoiceNumber) });
        }
        return invoiceArr;
      }, []);
      dispatch(setSelectedInvoicesDataToPay(tableData));
      // EA-1344: store the BASE total (no card fee). The processing fee is
      // card-country dependent and added in CheckoutForm once the card is
      // entered, so the summary shows the true invoice amount here.
      dispatch(setTotalStripeAmount(_total));
    }
  }, [selectedProjectInvoice]);

  const count = selectedInvoicesDataToPay.length;

  return (
    <Wrapper>
      <SectionLabel>Order Summary</SectionLabel>
      <Card>
        <CardHeader>
          <Heading>Selected Invoices</Heading>
          <CountPill>
            {count} {count === 1 ? 'invoice' : 'invoices'}
          </CountPill>
        </CardHeader>

        <HideOnPhone>
          <BigScreenTable invoices={selectedInvoicesDataToPay} />
        </HideOnPhone>
        <ShowOnPhone>
          <SmallScreenTable invoices={selectedInvoicesDataToPay} />
        </ShowOnPhone>

        <TotalRow>
          <TotalLabel>Invoice Total</TotalLabel>
          <TotalValue>{currency(totalStripeAmount)}</TotalValue>
        </TotalRow>

        <FeeNote>
          A processing fee will be added based on your card type. The exact amount will be shown before you confirm payment.
        </FeeNote>

        <Note>
          To avoid fees, you may send a check directly to our office before the due date. The office address is provided on the invoice. ACH transfer
          is not available at this time.
        </Note>
      </Card>
    </Wrapper>
  );
};

export default StripeInvoicesTable;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  min-width: 0;
`;

const SectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Card = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-sizing: border-box;

  @media only screen and (max-width: 540px) {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Heading = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    text-align: right;
    gap: 4px;
  }
`;

const TotalLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-600);
`;

const TotalValue = styled.span`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--success-700, #196a3b);
  font-variant-numeric: tabular-nums;
`;

// EA-1344: amber "a processing fee will be added…" disclosure, mirroring the
// epic-link FeeDisclosure banner.
const FeeNote = styled.div`
  font-size: 0.8125rem;
  color: #78350f;
  line-height: 1.5;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
`;

const Note = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.5;
  background: var(--neutral-50);
  border: 1px dashed var(--border);
  border-radius: 8px;
  padding: 12px 14px;
`;

const HideOnPhone = styled.div`
  @media only screen and (max-width: ${PHONE}) {
    display: none;
  }
`;

const ShowOnPhone = styled.div`
  display: none;
  @media only screen and (max-width: ${PHONE}) {
    display: block;
  }
`;
